if (!customElements.get('advanced-slider-product-section')) {
  class AdvancedSliderProductSection extends HTMLElement {
    constructor() {
      super();
      this.initImageSlider = this.initImageSlider.bind(this);
      this.initProductSlider = this.initProductSlider.bind(this);
      this.initVideoModals = this.initVideoModals.bind(this);
    }

    connectedCallback() {
      // Hero is above the fold and the image slider is type:'fade' — a fade slide only
      // becomes visible once Splide mounts (`.is-active`). The pre-init CSS shows the slide
      // box but the <img> stays hidden until then, so the hero LCP *waits for Splide to
      // mount*. Mount immediately (no requestIdleCallback): deferring the mount to idle
      // measurably delayed LCP (idle is starved by the marketing tag stack on the main
      // thread). splide loads on-demand via FoxTheme.loadSplide (snippets/splide-loader.liquid).
      const self = this;
      const go = () => {
        if (window.FoxTheme && typeof window.FoxTheme.loadSplide === 'function') {
          window.FoxTheme.loadSplide().then(() => self.init());
          return true;
        }
        if (typeof Splide !== 'undefined') {
          self.init();
          return true;
        }
        return false;
      };
      if (!go()) {
        // Loader snippet not parsed yet — retry briefly.
        let tries = 0;
        const t = setInterval(() => {
          if (go() || ++tries > 50) clearInterval(t);
        }, 50);
      }
    }

    init() {
      this.initImageSlider();
      this.initProductSlider();
      this.initVideoModals();
    }

    initVideoModals() {
      const videoBtns = this.querySelectorAll('.video-btn');
      if (videoBtns.length === 0) return;

      // Check if global modal exists, if not create it
      let modal = document.getElementById('asp-global-video-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'asp-global-video-modal';
        modal.className = 'asp-modal';
        modal.innerHTML = `
          <div class="asp-modal-content">
            <span class="asp-close-modal">&times;</span>
            <div class="asp-video-container" id="asp-video-container">
              <!-- Video will be injected here -->
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        // Global close handlers
        const closeBtn = modal.querySelector('.asp-close-modal');
        closeBtn.addEventListener('click', () => {
          modal.classList.remove('is-open');
          modal.style.display = 'none';
          const container = modal.querySelector('#asp-video-container');
          container.innerHTML = ''; // Stop video
        });

        window.addEventListener('click', (event) => {
          if (event.target === modal) {
            modal.classList.remove('is-open');
            modal.style.display = 'none';
            const container = modal.querySelector('#asp-video-container');
            container.innerHTML = ''; // Stop video
          }
        });
      }

      // Attach click handlers to buttons
      videoBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const videoUrl = btn.dataset.video;
          if (!videoUrl) return;

          const container = modal.querySelector('#asp-video-container');
          // Check extension to decide between video tag or iframe (if needed), 
          // but assuming file_url usually implies uploaded media.
          // Simple check for basic video extensions, default to video tag.

          container.innerHTML = `
            <video controls autoplay name="media" style="width:100%; height:100%;">
              <source src="${videoUrl}" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          `;

          modal.style.display = 'flex';
          // Small delay to allow display:flex to apply before adding opacity class for transition
          setTimeout(() => modal.classList.add('is-open'), 10);
        });
      });
    }

    initImageSlider() {
      const sectionId = this.dataset.sectionId;
      const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

      const sliderId = `#AspImageSlider-${sectionId}`;
      const sliderEl = this.querySelector(sliderId);
      if (!sliderEl) return;

      const autoplayEnabled = sliderEl.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(sliderEl.dataset.autoplayDelay) || 3000;

      // Custom Navigation
      const nextEl = this.querySelector(`.asp-next-img-${sectionId}`);
      const prevEl = this.querySelector(`.asp-prev-img-${sectionId}`);

      if (this.imageSplide) this.imageSplide.destroy();

      this.imageSplide = new Splide(sliderEl, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: false,
        autoplay: autoplayEnabled,
        interval: autoplayDelay,
        speed: 1000,
        direction: isRtl ? 'rtl' : 'ltr',
      });

      this.imageSplide.mount();

      // Bind Custom Arrows
      if (nextEl) nextEl.addEventListener('click', () => this.imageSplide.go('>'));
      if (prevEl) prevEl.addEventListener('click', () => this.imageSplide.go('<'));
    }

    initProductSlider() {
      const sectionId = this.dataset.sectionId;
      const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

      // Find all product sliders within this section (supports blocks)
      const productSliders = this.querySelectorAll('.asp-product-content .splide');

      productSliders.forEach(sliderEl => {
        if (sliderEl.dataset.splideInitialized) return; // Prevent double init

        const sliderId = sliderEl.id; // e.g., AspProductSlider-12345
        const blockId = sliderId.replace('AspProductSlider-', '');

        // Custom Navigation
        const nextEl = this.querySelector(`.asp-next-prod-${blockId}`);
        const prevEl = this.querySelector(`.asp-prev-prod-${blockId}`);

        const slideCount = sliderEl.querySelectorAll('.splide__slide').length;
        const enableLoop = slideCount > 1;

        // Mobile Settings
        const mobilePerPage = parseFloat(sliderEl.dataset.mobilePerPage) || 1;
        const mobileGap = (sliderEl.dataset.mobileGap || 10) + 'px';

        const splide = new Splide(sliderEl, {
          type: enableLoop ? 'loop' : 'slide',
          perPage: 1,
          gap: '20px',
          pagination: false,
          arrows: false, // We use custom arrows
          direction: isRtl ? 'rtl' : 'ltr',
          breakpoints: {
            768: {
              gap: mobileGap,
              perPage: mobilePerPage
            }
          }
        });

        splide.mount();
        sliderEl.dataset.splideInitialized = 'true';

        // Bind Custom Arrows
        if (nextEl) {
          // Remove old listeners if any (cloning node is a cheap way to clear listeners if needed, 
          // but here we just add new ones. Since we check initialized, it's fine).
          nextEl.addEventListener('click', () => splide.go('>'));
        }
        if (prevEl) {
          prevEl.addEventListener('click', () => splide.go('<'));
        }

        // Store instance reference if needed (maybe array if multiple)
        if (!this.productSplides) this.productSplides = [];
        this.productSplides.push(splide);
      });
    }

    disconnectedCallback() {
      if (this.imageSplide) this.imageSplide.destroy();
      if (this.productSplides) {
        this.productSplides.forEach(splide => splide.destroy());
      }
    }
  }

  customElements.define('advanced-slider-product-section', AdvancedSliderProductSection);
}
